/**
 * pages/me/lesson.js — Миний ХЭМАБ / Сургалт хэсэг.
 *
 * Хэрэглэгчийн сургалтын мэдээллийг **сургалтын төрлөөр (module_name)**
 * группэлж харуулна. Группыг collapse-аар хэрэглэгч задлан/нугалж үзнэ.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Collapse,
  Empty,
  Input,
  Select,
  Tag,
  Button,
} from "antd";
import ResponsiveTable from "src/components/ResponsiveTable";
import {
  CalendarOutlined,
  SearchOutlined,
  TagOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useUserContext } from "src/contexts/userContext";
import * as API from "src/api/request";
import _ from "lodash";
import moment from "moment";

const { Panel } = Collapse;

// Хувь хүний сургалтын жагсаалтад ЗӨВХӨН ОН харуулна — өдөр/сар нь
// ажилтанд ач холбогдолгүй, багана нарийсаж жагсаалт цэвэрхэн болно.
const fmtDate = (item) => {
  // ⚠ Мөр ирээгүй тохиолдлыг БАРИНА. Хүснэгтийн `render`-ийн эхний
  //   аргумент нь орчноос хамаарч өөр байж болно (antd — бүтэн мөр,
  //   хэрэв `dataIndex` заасан бол зөвхөн тэр талбарын утга). Хоосон
  //   үед бүх хуудсыг унагаахаас «—» харуулах нь хамаагүй дээр.
  if (!item) return "—";
  const beginYear = item.begin_date ? moment(item.begin_date).format("YYYY") : null;
  const endYear = item.end_date ? moment(item.end_date).format("YYYY") : null;
  if (!beginYear && !endYear) return "—";
  if (!endYear || beginYear === endYear) return beginYear || endYear;
  return `${beginYear} - ${endYear}`;
};

/**
 * Ажилтан тухайн сургалтад ҮНЭХЭЭР СУУСАН эсэх.
 *
 * `Register` мөр байх нь зөвхөн БҮРТГҮҮЛСЭН гэсэн үг. Оноо (`score`) орсон
 * эсвэл дүн гарсан (`resultdesc` нь "Дүгнээгүй" биш) бол суусанд тооцно.
 */
const attendedOf = (item) => {
  if (item?.score) return true;
  const d = String(item?.resultdesc || "");
  return !!d && d !== "Дүгнээгүй";
};

const renderStatus = (status) => {
  if (status === "Тэнцсэн") return <Tag color="green">Тэнцсэн</Tag>;
  if (status === "Тэнцээгүй") return <Tag color="red">Тэнцээгүй</Tag>;
  return <Tag color="default">{status || "—"}</Tag>;
};

// Тухайн module_name дээр оноо/тэнцсэн эсэх багана харуулах эсэх
const HAS_SCORE_MODULES = ["ХАБЭА", "Хүний хөгжлийн сургалт"];
const hasScore = (moduleName) => HAS_SCORE_MODULES.includes(moduleName);

// Сургалтын төрөл бүрд өвөрмөц өнгө + icon оноож харагдалтыг ялгана.
// Тодорхой module_name-д тохирох сонголт байхгүй бол hash-аас гаргана.
const MODULE_THEMES = {
  "ХАБЭА": {
    bg: "#FFF1F0",
    border: "#FFA39E",
    accent: "#CF1322",
    icon: "🛡️",
  },
  "Хүний хөгжлийн сургалт": {
    bg: "#F6FFED",
    border: "#B7EB8F",
    accent: "#389E0D",
    icon: "🌱",
  },
  "Мэргэжлийн сургалт": {
    bg: "#E6F4FF",
    border: "#91CAFF",
    accent: "#0958D9",
    icon: "🎓",
  },
  "Дотоод сургалт": {
    bg: "#FFF7E6",
    border: "#FFD591",
    accent: "#D46B08",
    icon: "🏢",
  },
  "Гадаад сургалт": {
    bg: "#F9F0FF",
    border: "#D3ADF7",
    accent: "#531DAB",
    icon: "🌐",
  },
  "Бусад": {
    bg: "#F5F5F5",
    border: "#D9D9D9",
    accent: "#595959",
    icon: "📚",
  },
};

// Fallback палитр — тодорхой бус module_name-д ашиглах
const FALLBACK_PALETTE = [
  { bg: "#E6FFFB", border: "#87E8DE", accent: "#08979C", icon: "📘" },
  { bg: "#FFF0F6", border: "#FFADD2", accent: "#C41D7F", icon: "📙" },
  { bg: "#FCFFE6", border: "#EAFF8F", accent: "#7CB305", icon: "📗" },
  { bg: "#FFFBE6", border: "#FFE58F", accent: "#D48806", icon: "📕" },
];

const hashIndex = (str, max) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
  return Math.abs(h) % max;
};

const getTheme = (moduleName) => {
  if (MODULE_THEMES[moduleName]) return MODULE_THEMES[moduleName];
  const idx = hashIndex(moduleName || "", FALLBACK_PALETTE.length);
  return FALLBACK_PALETTE[idx];
};

const Lesson = () => {
  const { user } = useUserContext();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Шүүлтүүд — огноо анхдагчаар "одоогийн он" (1-р сарын 1 → өнөөдөр).
  // Хэрэглэгч clear (X) дарж бүх огноог харах боломжтой.
  const [filterRange, setFilterRange] = useState([
    moment().startOf("year"),
    moment(),
  ]);
  const [filterModule, setFilterModule] = useState(undefined);
  const [filterName, setFilterName] = useState("");

  // «Суух ёстой сургалт» самбарын ОН — солиход доорх ҮНДСЭН ЖАГСААЛТ ч
  // тухайн оноор шүүгдэнэ (өмнө нь 2 хяналт тус тусдаа ажиллаж, он солиход
  // жагсаалт өөрчлөгдөхгүй байгаа мэт харагдаж байв).
  const [planYear, setPlanYear] = useState(() => moment().year());

  // ТӨЛӨВЛӨЛТ ба СУУСАН СУРГАЛТ нь ӨӨР ЗҮЙЛ — нэг дор хольж харуулахгүй,
  // дээд талын шилжүүлэгчээр сонгоно.
  //   plan → жилийн төлөвлөгөө (суух ёстой) — training.PlanWorker + Norm
  //   done → бодитоор бүртгэгдсэн/суусан сургалт — training.Register
  const [view, setView] = useState("plan");
  const thisYear = moment().year();
  const onPlanYearChange = (y) => {
    setPlanYear(y);
    setFilterRange([
      moment(String(y), "YYYY").startOf("year"),
      moment(String(y), "YYYY").endOf("year"),
    ]);
  };

  useEffect(() => {
    if (user.tn !== 0) {
      setLoading(true);
      API.getEmployeeTraining()
        .then((res) => setList(Array.isArray(res) ? res : []))
        .catch(() => setList([]))
        .finally(() => setLoading(false));
    }
  }, [user.tn]);

  // Сонгох боломжтой module_name болон trainingname-уудын жагсаалт (Select-д)
  const moduleOptions = useMemo(() => {
    const set = new Set();
    list.forEach((l) => l.module_name && set.add(l.module_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "mn"));
  }, [list]);

  const nameOptions = useMemo(() => {
    const set = new Set();
    list.forEach((l) => {
      const name = l.trainingname;
      if (name && (!filterModule || l.module_name === filterModule)) {
        set.add(name);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "mn"));
  }, [list, filterModule]);

  // Шүүсэн жагсаалт — огноо/төрөл/нэр
  const filteredList = useMemo(() => {
    const [start, end] = filterRange || [];
    return list.filter((item) => {
      if (start || end) {
        const d = item.begin_date
          ? moment(item.begin_date)
          : item.end_date
            ? moment(item.end_date)
            : null;
        if (!d) return false;
        if (start && d.isBefore(start, "day")) return false;
        if (end && d.isAfter(end, "day")) return false;
      }
      if (filterModule && item.module_name !== filterModule) return false;
      if (filterName) {
        const name = String(item.trainingname || "").toLowerCase();
        if (!name.includes(filterName.toLowerCase())) return false;
      }
      return true;
    });
  }, [list, filterRange, filterModule, filterName]);

  // module_name-аар группэлж, тоо болон тэнцсэн/тэнцээгүй хүртэлх агрегат
  const groups = useMemo(() => {
    const byModule = {};
    filteredList.forEach((item) => {
      const key = item.module_name || "Бусад";
      if (!byModule[key]) {
        byModule[key] = {
          moduleName: key,
          items: [],
          passed: 0,
          failed: 0,
        };
      }
      byModule[key].items.push(item);
      if (item.resultdesc === "Тэнцсэн") byModule[key].passed += 1;
      else if (item.resultdesc === "Тэнцээгүй") byModule[key].failed += 1;
    });
    return Object.values(byModule).sort((a, b) =>
      a.moduleName.localeCompare(b.moduleName, "mn"),
    );
  }, [filteredList]);

  // Бүртгэгдсэн мөрөөс ҮНЭХЭЭР СУУСАН нь хэд вэ (шилжүүлэгчийн тоолуурт)
  const attendedCount = useMemo(
    () => filteredList.filter(attendedOf).length,
    [filteredList],
  );

  // Огнооны шүүлт хассан тул зөвхөн төрөл/нэрийг цэвэрлэнэ (он нь дээд
  // самбарын сонголтоор удирдагдана).
  const hasFilter = filterModule || filterName;

  const clearFilters = () => {
    setFilterModule(undefined);
    setFilterName("");
  };

  // Сургалтын төрлийн хувьд тохиромжтой багана үүсгэх
  const buildColumns = (moduleName) => {
    const cols = [
      {
        title: "№",
        width: 50,
        align: "center",
        render: (_t, _r, i) => i + 1,
      },
      {
        title: "Он",
        width: 90,
        align: "center",
        render: (item) => fmtDate(item),
      },
      {
        title: "Нэр",
        dataIndex: "trainingname",
      },
      // Бүртгүүлсэн ≠ суусан. Оноо/дүн орсон бол СУУСАН, эс бөгөөс
      // бүртгэгдсэн ч суугаагүй (дүгнэгдээгүй) гэж ялгана.
      {
        title: "Суусан эсэх",
        width: 110,
        align: "center",
        render: (item) =>
          attendedOf(item) ? (
            <Tag color="green">Суусан</Tag>
          ) : (
            <Tag color="orange">Суугаагүй</Tag>
          ),
      },
    ];
    if (hasScore(moduleName)) {
      cols.push(
        {
          title: "Авах оноо",
          dataIndex: "scoreplan",
          width: 100,
          align: "center",
          render: (v) => (v != null ? _.round(v, 1) : "—"),
        },
        {
          title: "Авсан оноо",
          dataIndex: "score",
          width: 100,
          align: "center",
          render: (v) => (v ? _.round(v, 1) : "—"),
        },
        {
          title: "Хувь",
          dataIndex: "result",
          width: 80,
          align: "center",
          render: (v) => (v != null ? `${_.round(v, 1)}%` : "—"),
        },
        {
          title: "Тэнцсэн эсэх",
          width: 120,
          align: "center",
          render: (item) =>
            item.score ? renderStatus(item.resultdesc) : <Tag>—</Tag>,
        },
      );
    }
    return cols;
  };

  if (loading) {
    return <div style={{ padding: 20, textAlign: "center" }}>Ачааллаж байна...</div>;
  }

  // Шүүлтийн UI — list хоосон ч хэрэглэгчид яагаад эхэлж сонголтонд харагдана
  const filterPanel = (
    <Card
      size="small"
      bodyStyle={{ padding: 12 }}
      style={{ marginBottom: 12 }}
    >
      {/* ⚠ RESPONSIVE (2026-09-30): өмнө нь сонгогчид `minWidth` 220/260
          гэж ХАТУУ баригдсан тул утсан дээр (≈360px) шахагдаж чадалгүй
          хальж, хэвтээ гүйлгэх зурвас гаргадаг байв.
          Одоо `flex-basis`-ээр: өргөн дэлгэцэд байгалийн хэмжээтэй,
          нарийнд бүтэн мөр эзлэн доош шилжинэ. `minWidth: 0` нь flex
          элемент агуулгаасаа доош шахагдахыг ЗӨВШӨӨРНӨ — үүнгүйгээр
          flex-basis дангаараа хангалтгүй. */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Эхлэх/дуусах огнооны шүүлт БАЙХГҮЙ — ажилтны сургалтыг зөвхөн
            ОНООР шүүнэ. Оныг дээд самбарын («Суух ёстой сургалт») он-сонголт
            удирдана. */}
        <Select
          showSearch
          allowClear
          placeholder={
            <span>
              <TagOutlined style={{ marginRight: 6 }} />
              Сургалтын төрөл
            </span>
          }
          value={filterModule}
          onChange={(v) => {
            setFilterModule(v);
            // Төрөл солихоор нэр сонголтыг арилгана
            setFilterName("");
          }}
          options={moduleOptions.map((m) => ({ label: m, value: m }))}
          style={{ flex: "1 1 190px", minWidth: 0, maxWidth: "100%" }}
        />
        <Select
          showSearch
          allowClear
          placeholder={
            <span>
              <SearchOutlined style={{ marginRight: 6 }} />
              Сургалтын нэр
            </span>
          }
          value={filterName || undefined}
          onChange={(v) => setFilterName(v || "")}
          options={nameOptions.map((n) => ({ label: n, value: n }))}
          style={{ flex: "2 1 220px", minWidth: 0, maxWidth: "100%" }}
          optionFilterProp="label"
          notFoundContent={
            moduleOptions.length === 0
              ? "Сургалт алга"
              : filterModule
                ? "Энэ төрөлд тохирох сургалт олдсонгүй"
                : "Сургалт олдсонгүй"
          }
        />
        {hasFilter && (
          <Button
            size="small"
            type="link"
            icon={<ReloadOutlined />}
            onClick={clearFilters}
          >
            Шүүлт цэвэрлэх
          </Button>
        )}
        {/* Үр дүнгийн тоо — ҮРГЭЛЖ баруун захад (`marginLeft:auto`).
            ⚠ `whiteSpace:nowrap` ЗААВАЛ: «Үр дүн: 12 / нийт 40» гэсэн мөр
              нарийн дэлгэцэд дунд нь тасарч хоёр мөр болдог байв. Тоо нь
              нэг бүхэл утга тул таслагдвал уншихад төөрөлдүүлнэ. */}
        <span
          style={{
            marginLeft: "auto",
            flex: "0 1 auto",
            fontSize: 12,
            color: "#595959",
            whiteSpace: "nowrap",
          }}
        >
          Үр дүн:{" "}
          <b style={{ color: "#2C2C2A" }}>{filteredList.length}</b>
          {filteredList.length !== list.length && (
            <span style={{ color: "#9A988F" }}> / нийт {list.length}</span>
          )}
        </span>
      </div>
    </Card>
  );

  // ── Дээд шилжүүлэгч — ТӨЛӨВЛӨЛТ / СУУСАН СУРГАЛТ ────────────────────────
  const viewSwitch = (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginBottom: 10,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {[
        { key: "plan", label: "Төлөвлөлт", hint: "суух ёстой" },
        {
          key: "done",
          label: `Суусан сургалт (${attendedCount})`,
          hint: "бүртгэгдсэн/дүн",
        },
      ].map((t) => {
        const active = view === t.key;
        return (
          <Button
            key={t.key}
            type={active ? "primary" : "default"}
            onClick={() => setView(t.key)}
            style={
              active
                ? { background: "#0F6E56", borderColor: "#0F6E56" }
                : undefined
            }
          >
            {t.label}
            <span
              style={{
                marginLeft: 6,
                fontSize: 11,
                opacity: 0.75,
                fontWeight: 400,
              }}
            >
              · {t.hint}
            </span>
          </Button>
        );
      })}

      {/* ОН — 2 харагдацад ЕРӨНХИЙ. Төлөвлөгөө болон суусан сургалтын
          жагсаалт хоёул үүгээр шүүгдэнэ. */}
      <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
        <CalendarOutlined style={{ color: "#8A94A6" }} />
        <Select
          size="small"
          value={planYear}
          onChange={onPlanYearChange}
          style={{ width: 110 }}
          options={[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map(
            (y) => ({ value: y, label: `${y} он` }),
          )}
        />
      </span>
    </div>
  );

  // Төлөвлөлтийн харагдац — суусан сургалтын жагсаалт огт орохгүй
  if (view === "plan") {
    return (
      <div>
        {viewSwitch}
        <PendingTrainingPlan year={planYear} />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div>
        {viewSwitch}
        {filterPanel}
        <Empty
          description={
            hasFilter
              ? "Шүүлтэд тохирох сургалт алга"
              : "Сургалтын мэдээлэл олдсонгүй"
          }
          style={{ padding: 30 }}
        >
          {hasFilter && (
            <Button
              type="link"
              icon={<ReloadOutlined />}
              onClick={clearFilters}
            >
              Шүүлт цэвэрлэх
            </Button>
          )}
        </Empty>
      </div>
    );
  }

  return (
    <div>
      {viewSwitch}
      {filterPanel}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {groups.map((g) => {
        const theme = getTheme(g.moduleName);
        return (
          <Collapse
            key={g.moduleName}
            defaultActiveKey={[g.moduleName]}
            expandIconPosition="start"
            style={{
              background: theme.bg,
              border: `1.5px solid ${theme.border}`,
              borderLeft: `5px solid ${theme.accent}`,
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            <Panel
              key={g.moduleName}
              header={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: theme.accent,
                    }}
                  >
                    {theme.icon} {g.moduleName}
                  </span>
                  <span
                    style={{
                      background: theme.accent,
                      color: "#fff",
                      borderRadius: 999,
                      padding: "2px 10px",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {g.items.length} сургалт
                  </span>
                  {hasScore(g.moduleName) && (g.passed > 0 || g.failed > 0) && (
                    <>
                      {g.passed > 0 && (
                        <Tag color="green" style={{ margin: 0 }}>
                          ✓ Тэнцсэн: {g.passed}
                        </Tag>
                      )}
                      {g.failed > 0 && (
                        <Tag color="red" style={{ margin: 0 }}>
                          ✗ Тэнцээгүй: {g.failed}
                        </Tag>
                      )}
                    </>
                  )}
                </div>
              }
              style={{ background: "#fff" }}
            >
              <ResponsiveTable
                rowKey={(item) =>
                  item.id ||
                  `${g.moduleName}|${item.trainingname || ""}|${item.begin_date || ""}|${item.end_date || ""}|${item.score || ""}`
                }
                size="small"
                columns={buildColumns(g.moduleName)}
                dataSource={g.items}
                pagination={false}
                scroll={{ x: "max-content" }}
              />
            </Panel>
          </Collapse>
        );
      })}
      </div>
    </div>
  );
};

export default Lesson;

/* ────────────────────────────────────────────────────────────────────────
 *  PendingTrainingPlan — ЖИЛИЙН ТӨЛӨВЛӨГӨӨНД байгаа ч ажилтан хараахан
 *  СУУГААГҮЙ сургалтууд, тэдгээрийн ХУВААРЬ.
 *
 *  Эх сурвалж: сургалтын систем (training.erdenetmc.mn → /plan) дэх
 *  `training.PlanWorker` (жилийн «Нийт ажилтны сургалт»-д хамрагдах ажилтан)
 *  ба `training.Register` (бодитоор суусан бүртгэл). Хоёрын зөрүүг backend
 *  `/employee/training/plan` дээр бодож буцаана.
 * ──────────────────────────────────────────────────────────────────────── */
const PendingTrainingPlan = ({ year }) => {
  const { user } = useUserContext();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Хүсэлт амжилтгүй болсныг ХООСОН-оос ялгана — эс бөгөөс backend унасан
  // үед «бүрэн хамрагдсан» гэсэн ногоон мессеж худал харагдана.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (user.tn === 0) return;
    let alive = true;
    setLoading(true);
    setFailed(false);
    API.getEmployeeTrainingPlan(year)
      .then((res) => {
        if (!alive) return;
        if (Array.isArray(res)) setRows(res);
        else {
          // Backend {message: ...} буцаасан — алдаа
          setRows([]);
          setFailed(true);
        }
      })
      .catch(() => {
        if (!alive) return;
        setRows([]);
        setFailed(true);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [user.tn, year]);

  const total = rows.reduce((s, r) => s + (r.remaining || 0), 0);

  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        borderLeft: "5px solid #CA8A04",
        background: "#FFFBEB",
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <CalendarOutlined style={{ color: "#CA8A04" }} />
          <b style={{ fontSize: 13 }}>Төлөвлөгдсөн сургалт</b>
          <Tag color="blue">{rows.length}</Tag>
          {total > 0 && <Tag color="orange">дутуу {total}</Tag>}
          <span style={{ fontSize: 11.5, color: "#8A94A6" }}>
            {year} оны төлөвлөгөө
          </span>
        </div>
      }
      /* Он сонголт нь ДЭЭД талын ерөнхий хяналтад шилжсэн — энд давхардуулахгүй */
    >
      {loading ? (
        <div style={{ padding: 12, color: "#A8AFBC", fontSize: 12 }}>
          Уншиж байна...
        </div>
      ) : failed ? (
        <div style={{ padding: 12, color: "#DC2626", fontSize: 12.5 }}>
          <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }} />
          Сургалтын төлөвлөгөөг татаж чадсангүй. Дахин оролдоно уу.
        </div>
      ) : rows.length === 0 ? (
        // Хоосон = ТӨЛӨВЛӨГӨӨ ҮҮСЭЭГҮЙ. «Бүрэн хамрагдсан» гэж хэлж
        // БОЛОХГҮЙ — endpoint нь төлөвлөгөөний бүх мөрийг (суусан ч,
        // дутуу ч) буцаадаг тул хоосон гэдэг нь мөр огт байхгүй гэсэн үг.
        <div style={{ padding: 12, color: "#8A94A6", fontSize: 12.5 }}>
          <i className="fas fa-info-circle" style={{ marginRight: 6 }} />
          {year} онд танд төлөвлөгөөт сургалт бүртгэгдээгүй байна.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Төлөвлөгөө байгаа БА бүгд хамрагдсан үед л ногоон мэдэгдэл */}
          {total === 0 && (
            <div
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                background: "#F1FAF4",
                border: "1px solid #D8EEE1",
                color: "#16A34A",
                fontSize: 12.5,
                fontWeight: 600,
              }}
            >
              <i className="fas fa-check-circle" style={{ marginRight: 6 }} />
              {year} оны төлөвлөгөөт сургалтууд бүрэн хамрагдсан байна.
            </div>
          )}
          {rows.map((r) => {
            const done = (r.remaining || 0) <= 0;
            return (
            <div
              key={r.type_id}
              style={{
                background: "#fff",
                // Суусан нь ногоон, дутуу нь шар зурвастай — нэг харцаар ялгана
                borderLeft: `4px solid ${done ? "#16A34A" : "#CA8A04"}`,
                border: "1px solid " + (done ? "#D8EEE1" : "#FDE68A"),
                borderLeftWidth: 4,
                borderLeftColor: done ? "#16A34A" : "#CA8A04",
                borderRadius: 8,
                padding: "8px 10px",
                opacity: done ? 0.85 : 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 13, color: "#1F2430" }}>
                  {r.type_name || "Нэргүй сургалт"}
                </span>
                {r.module_name && <Tag color="gold">{r.module_name}</Tag>}
                {/* Давтамж — сургалтын вэбийн /plan дээрхтэй ижил */}
                {r.interval_name && <Tag>{r.interval_name}</Tag>}
                {done ? (
                  <Tag color="green">Хамрагдсан</Tag>
                ) : (
                  <Tag color="orange">Дутуу {r.remaining}</Tag>
                )}
                <span style={{ fontSize: 11.5, color: "#8A94A6" }}>
                  Төлөвлөсөн {r.planned} · суусан {r.attended}
                  {r.last_register_date && (
                    <>
                      {" · сүүлд суусан "}
                      <b style={{ color: "#5A6478" }}>
                        {moment(r.last_register_date).format("YYYY.MM.DD")}
                      </b>
                    </>
                  )}
                </span>
              </div>

              {/* Хуваарь — ЗӨВХӨН ИРЭХ хичээлүүд. Тухайн сургалтын төрөл
                  дээр жилд 50+ удаа хичээл зарлагддаг ба өнгөрсөн огноо
                  ажилтанд хэрэггүй тул зөвхөн тоогоор нь дурдана. */}
              <ScheduleLine schedule={r.schedule} />
            </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

/**
 * ScheduleLine — сургалтын хуваарийг ажилтанд ХЭРЭГТЭЙ хэлбэрээр.
 *
 * Сургалтын систем нь нэг төрөл дээр жилд олон арван хичээл (бүх ээлж,
 * бүлэг) зарладаг. Ажилтан өнгөрсөн хичээлд суух боломжгүй тул зөвхөн
 * ИРЭХ огноог харуулж, өнгөрснийг тоогоор нь дурдана.
 */
const SCHEDULE_SHOW = 6;

const ScheduleLine = ({ schedule }) => {
  const [expanded, setExpanded] = useState(false);
  const all = Array.isArray(schedule) ? schedule : [];

  if (all.length === 0) {
    return (
      <div style={{ marginTop: 6, fontSize: 11.5, color: "#A8AFBC" }}>
        Хуваарь хараахан зарлагдаагүй байна.
      </div>
    );
  }

  const today = moment().startOf("day");
  const dateOf = (s) => (s.begin_date ? moment(s.begin_date) : null);
  const endOf = (s) => (s.end_date ? moment(s.end_date) : dateOf(s));

  const upcoming = all.filter((s) => {
    const e = endOf(s);
    return e ? !e.isBefore(today, "day") : true;
  });
  const pastCount = all.length - upcoming.length;

  if (upcoming.length === 0) {
    return (
      <div style={{ marginTop: 6, fontSize: 11.5, color: "#A8AFBC" }}>
        Ирэх хуваарь алга — энэ жил {pastCount} удаа зохион байгуулагдсан.
        Хуваарь зарлагдахыг хүлээнэ үү.
      </div>
    );
  }

  const shown = expanded ? upcoming : upcoming.slice(0, SCHEDULE_SHOW);
  const rest = upcoming.length - shown.length;

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 11, color: "#8A94A6", marginBottom: 4 }}>
        Ирэх хуваарь ({upcoming.length})
        {pastCount > 0 && (
          <span> · өнгөрсөн {pastCount} хичээлийг харуулаагүй</span>
        )}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {shown.map((s) => {
          const b = dateOf(s);
          const e = s.end_date ? moment(s.end_date) : null;
          // Долоо хоногийн дотор бол улаан — яаралтай бүртгүүлэх шаардлагатай
          const soon = b && b.diff(today, "days") <= 7;
          return (
            <Tag
              key={s.lesson_id}
              color={soon ? "red" : "blue"}
              style={{ marginInlineEnd: 0 }}
            >
              <CalendarOutlined style={{ marginRight: 4 }} />
              {b ? b.format("YYYY.MM.DD") : "—"}
              {e && (!b || !e.isSame(b, "day"))
                ? ` - ${e.format("MM.DD")}`
                : ""}
            </Tag>
          );
        })}
        {rest > 0 && (
          <Tag
            onClick={() => setExpanded(true)}
            style={{ marginInlineEnd: 0, cursor: "pointer" }}
          >
            +{rest} бусад
          </Tag>
        )}
      </div>
    </div>
  );
};
